<template>
  <Content
    class="order-list"
    aside-stretched
    full-width
    remove-card-wrapper
    remove-aside-card-padding
  >
    <template #header>
      <div
        class="content-header flex justify-content-between align-items-center"
      >
        <div
          class="content-heading-container flex flex-gap-1 align-items-center"
        >
          <SavedSearchControls
            v-model:selected-search="selectedSearch"
            :edited="isEdited"
            :popular-searches="popularSearches"
            :saved-searches="savedSearches"
            :header-text="t('orders.orders', 2)"
            header-icon="file-alt"
            @clear="onSavedSearchClear"
            @delete="
              requestDeleteConfirmation({
                param: t('search.savedSearches'),
                accept: () => onSavedSearchDelete($event.search),
              })
            "
            @discard="onSavedSearchDiscard"
            @remove-as-default="onRemoveSearchAsDefault"
            @save-as-default="onSaveSearchAsDefault"
            @save="saveSearch"
            @select="onSavedSearchSelect"
            @update="onSaveSearchUpdate"
          />
          <LoadingIndicator
            :loading="results.loading || isBatchActionLoading"
          />
        </div>

        <span class="flex flex-gap-1 align-items-center">
          <InputWrapper
            v-if="isElasticsearchEnabledFeatureFlag"
            v-slot="{ inputClasses, inputId }"
            input-id="use-new-search-switch"
            :label="t('search.useNewSearch')"
            :label-help-text="t('search.useNewSearchTooltip')"
            label-icon="pi pi-info-circle"
            class="flash-info-light"
          >
            <RygenSwitch
              :id="inputId"
              v-model="isElasticsearchEnabledUserPreference"
              :class="inputClasses"
              @change="loadColumnsAndSearch(columnsLSKey)"
            />
          </InputWrapper>

          <OrderExportSearchResult
            :criteria="criteria"
            :visible-columns="columns"
          />
          <RygenButton
            as="router-link"
            mode="secondary"
            :to="{ name: 'importOrders' }"
            :label="
              t('common.importParam', { param: t('orders.orders', 2) }, 2)
            "
            icon="pi pi-upload"
          />
          <OrderBatchActionButton
            :selected-data="selectedRows"
            :loading="isBatchActionLoading"
            data-testid="order-list-batch-action-button"
            @command="onBatchActionCommand"
          />
          <RygenButton
            v-if="$hasAuthority('order', 'create')"
            as="router-link"
            :to="{ name: 'orderEntry' }"
            :label="t('common.newParam', { param: t('orders.orders') })"
            icon="pi pi-plus"
            icon-pos="right"
            data-testid="order-list-new-button"
          />
        </span>
      </div>
    </template>

    <SearchResultTableAndFilterWrapper
      :class="{ 'new-search-toggle': isElasticsearchEnabledFeatureFlag }"
    >
      <template #filters>
        <OrderSearchBuilder
          id="order-search-builder"
          :model-value="criteria"
          @update:model-value="onCriteriaChange"
        />
      </template>

      <template #default>
        <NewOrderSearchResultView
          v-if="isElasticsearchEnabled"
          v-model:columns="columns"
          v-model:selection="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            selectedRows as EsOrder[]
          "
          :order-search-criteria="criteria"
          :results="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            results as Loadable<SearchResult<EsOrder>>
          "
          :handling-command="isBatchActionLoading"
          :column-options="availableColumns"
          @page="onPage"
          @sort="onSort"
          @command="onBatchActionCommand"
          @edited-columns="onEditedColumns"
          @refresh-no-cache="onRefreshNoCache"
        />
        <OrderSearchResultView
          v-else
          v-model:columns="columns"
          v-model:selection="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            selectedRows as OrderSearchData[]
          "
          :order-search-criteria="criteria"
          :results="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            results as Loadable<SearchResult<OrderSearchData>>
          "
          :handling-command="isBatchActionLoading"
          :column-options="availableColumns"
          @page="onPage"
          @sort="onSort"
          @command="onBatchActionCommand"
          @edited-columns="onEditedColumns"
          @refresh-no-cache="onRefreshNoCache"
        />
      </template>
    </SearchResultTableAndFilterWrapper>

    <EditTagsModal
      v-model:visible="showEditTagsModal"
      :tag-options="tagOptions"
      @save="onBatchAddTagsConfirm"
    />

    <OrderCancellationModal
      v-model:visible="isCancellationModalVisible"
      :num-orders-selected="selectedRows.length"
      @cancel-order="onBatchCancelConfirm"
    />

    <BatchCreateSuccessModal
      v-model:visible="batchCloneSuccessVm.visible"
      :ids="batchCloneSuccessVm.orderIds"
      type="Order"
    />

    <InviteCarriersToSpotMarketModal
      v-model:visible="inviteCarriersToSpotMarketModal.visible"
      :invite-command="inviteCarriersToSpotMarketModal.inviteCommand"
      :can-view-uplift="canViewUplift"
      @submit="onInviteCarriersToSpotMarketConfirm"
    />

    <SpotMarketErrorModal
      v-model:visible="spotMarketErrorModal.visible"
      :spot-markets="spotMarketErrorModal.erroredSpotMarkets"
    />

    <OrderCloneModal
      v-model:visible="batchCloneOrderModal.visible"
      v-model:clone-reference-numbers="
        batchCloneOrderModal.cloneReferenceNumbers
      "
      v-model:clone-tags="batchCloneOrderModal.cloneTags"
      v-model:skip-auto-actions="batchCloneOrderModal.skipAutoActions"
      :num-orders-selected="selectedRows.length"
      :show-clone-tags-option="batchCloneOrderModal.showTagsOption"
      :show-clone-reference-numbers-option="
        batchCloneOrderModal.showRefernceNumberOption
      "
      @confirm="onBatchCloneConfirm"
    />

    <AssignUsersModal
      v-model:visible="assignUsersModal.visible"
      :assignable-users="assignUsersModal.assignableUsers"
      @save="onBatchAssignUsersConfirm"
    />
  </Content>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  ToastMessage,
  useDeleteConfirmation,
  usePartitionedStorageKey,
  useQueryParamsForSearch,
  useToast,
} from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  ClientReferenceNumberType,
  ColumnSpecification,
  DropdownOption,
  Loadable,
  ReferenceNumberTypeLevel,
  SearchResult,
  UserRef,
} from '@rygen/rygen-ui/model';
import { distinct, distinctBy, notNull } from '@rygen/rygen-ui/util';
import { Order as EsOrder } from '@rygen/tms-search-model/search/order';
import { ReferenceNumber as EsReferenceNumber } from '@rygen/tms-search-model/search/referenceNumber';

import api from '@/api';
import {
  useElasticsearch,
  useLocationGrouping,
  useReferenceNumberTypes,
  useSavedSearches,
} from '@/composables';
import {
  CancelOrderPayload,
  ColumnProps,
  ES_ORDER_COLUMNS,
  EsOrderColumnProps,
  InviteCommand,
  isShipmentColumnProps,
  Order,
  ORDER_COLUMNS,
  OrderAction,
  OrderInviteCarriersToSpotMarketBatchCommand,
  OrderSavedSearch,
  OrderSearchCriteria,
  OrderSearchData,
  OrderSearchResult,
  ReferenceNumber,
  SpotMarket,
  UserAssignmentType,
} from '@/model';
import { useAssignableUserStore } from '@/stores/AssignableUser';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLocationGroupStore } from '@/stores/LocationGroup';
import { useSpotMarketStore } from '@/stores/SpotMarket';
import { useTagStore } from '@/stores/Tag';
import { formatReferenceNumberValue } from '@/util';

import InviteCarriersToSpotMarketModal from '@/components/carrierPortal/InviteCarriersToSpotMarketModal.vue';
import SpotMarketErrorModal from '@/components/carrierPortal/SpotMarketErrorModal.vue';
import NewOrderSearchResultView from '@/components/order/NewOrderSearchResult.vue';
import OrderBatchActionButton from '@/components/order/OrderBatchActionButton.vue';
import OrderCancellationModal from '@/components/order/OrderCancellationModal.vue';
import OrderCloneModal from '@/components/order/OrderCloneModal.vue';
import OrderExportSearchResult from '@/components/order/OrderExportSearchResult.vue';
import OrderSearchBuilder from '@/components/order/OrderSearchBuilder.vue';
import OrderSearchResultView from '@/components/order/OrderSearchResult.vue';
import BatchCreateSuccessModal from '@/components/ui/BatchCreateSuccessModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenSwitch from '@rygen/rygen-ui/components/form/RygenSwitch.vue';
import SavedSearchControls from '@rygen/rygen-ui/components/search/SavedSearchControls.vue';
import EditTagsModal from '@rygen/rygen-ui/components/tag/EditTagsModal.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import SearchResultTableAndFilterWrapper from '@rygen/rygen-ui/components/ui/SearchResultTableAndFilterWrapper.vue';
import AssignUsersModal from '@rygen/rygen-ui/components/user/AssignUsersModal.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

/**
 * The number in milliseconds to wait after a batch action before refreshing
 * search results
 **/
const BATCH_ACTION_DELAY = 5000;

const {
  isElasticsearchEnabled,
  isElasticsearchEnabledFeatureFlag,
  isElasticsearchEnabledUserPreference,
} = useElasticsearch({ searchType: 'order' });
const { filterLocationGroupColumns, isLocationGroupingEnabled } =
  useLocationGrouping();
const { loadLocationGroups } = useLocationGroupStore();
const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const { currentUser, selectedDomain } = useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { t } = useI18n();
const { storageKey: columnsLSKey } = usePartitionedStorageKey('orderColumns', {
  currentUser,
  selectedDomain,
});

const runSearch = async (
  criteria: OrderSearchCriteria,
  cancelRequest?: CancelTokenSource,
) => {
  try {
    results.value.loading = true;
    results.value.data = isElasticsearchEnabled.value
      ? await api.order.searchWithElasticSearch(criteria, {
          cancelToken: cancelRequest,
          useCache: useCache.value,
        })
      : await api.order.search(criteria, {
          cancelToken: cancelRequest,
          useCache: useCache.value,
        });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error loading orders', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.orders', 2),
      }),
      error: e,
    });
  } finally {
    results.value.loading = false;
    useCache.value = true;
  }
};

const { criteria, runSearchDebounced, searchQueryString } =
  useQueryParamsForSearch({
    factoryMethod: (opts?: Partial<OrderSearchCriteria>) =>
      new OrderSearchCriteria(opts),
    routeName: 'orderList',
    skipSearchOnMount: true,
    runSearch,
  });

const {
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
  orderReferenceNumberTypes,
} = useReferenceNumberTypes();
const {
  createSavedSearch,
  deleteSavedSearch,
  loadPopularSearches,
  loadSavedSearches,
  popularSearches,
  savedSearches,
  updateSavedSearch,
} = useSavedSearches('Order');
const { inviteCarriersToSpotMarket } = useSpotMarketStore();
const { loadTags, tagOptions } = useTagStore();
const toast = useToast();

const columns: Ref<ColumnSpecification[]> = ref(
  filterLocationGroupColumns(
    isElasticsearchEnabled.value ? ES_ORDER_COLUMNS : ORDER_COLUMNS,
  ),
);
const selectedRows: Ref<OrderSearchData[] | EsOrder[]> = ref([]);
const results: Ref<Loadable<SearchResult<OrderSearchData | EsOrder>>> = ref({
  loading: false,
  data: new OrderSearchResult(),
});
const selectedSearch: Ref<OrderSavedSearch | undefined> = ref();
const isEdited = ref(false);
const isBatchActionLoading = ref(false);
const showEditTagsModal = ref(false);
const isCancellationModalVisible = ref(false);
const useCache = ref(true);
const canViewUplift = ref(false);

const batchCloneOrderModal = ref({
  visible: false,
  cloneReferenceNumbers: false,
  cloneTags: false,
  skipAutoActions: false,
  showRefernceNumberOption: false,
  showTagsOption: false,
});
const inviteCarriersToSpotMarketModal = ref({
  visible: false,
  inviteCommand: new OrderInviteCarriersToSpotMarketBatchCommand(),
});
const spotMarketErrorModal = ref({
  visible: false,
  erroredSpotMarkets: [] as SpotMarket[],
});
const batchCloneSuccessVm = ref({
  visible: false,
  orderIds: [] as number[],
});
const assignUsersModal = ref({
  visible: false,
  assignableUsers: { loading: false } as Loadable<UserRef[]>,
});

const referenceNumbers: Ref<ClientReferenceNumberType[]> = computed(() => {
  const orderTypes = orderReferenceNumberTypes.value.data ?? [];
  const lineItemTypes = lineItemReferenceNumberTypes.value.data ?? [];

  return [...orderTypes, ...lineItemTypes].filter(distinct()).sort((a, b) => {
    const aName = a.referenceNumberType?.name ?? '';
    const bName = b.referenceNumberType?.name ?? '';
    return aName < bName ? -1 : aName > bName ? 1 : 0;
  });
});

const referenceNumberValueOptionsMap = computed(() => {
  const entries = referenceNumbers.value
    .map<
      | [naturalId: string, valueOptionMap: Map<any, DropdownOption<any>>]
      | undefined
    >((clientRefNum) => {
      const refNumType = clientRefNum.referenceNumberType;
      if (!refNumType?.valueOptions || !refNumType.naturalId) {
        return undefined;
      }

      const valueOptionsEntries: [any, DropdownOption<any>][] =
        refNumType.valueOptions.map((opt) => [opt.value, opt]) ?? [];
      return [refNumType.naturalId, new Map(valueOptionsEntries)];
    })
    .filter(notNull);

  return new Map(entries);
});

const referenceNumberColumns = computed(() => {
  return (
    referenceNumbers.value.map((clientRefNum) => {
      const refNumNaturalId = clientRefNum.referenceNumberType?.naturalId!;
      const isLineItemType = clientRefNum.levels?.includes(
        ReferenceNumberTypeLevel.LINE_ITEM,
      );
      const isOrderType = clientRefNum.levels?.includes(
        ReferenceNumberTypeLevel.ORDER,
      );

      return new ColumnSpecification({
        key: refNumNaturalId,
        field: refNumNaturalId,
        header: clientRefNum.referenceNumberType?.name,
        sortable: true,
        body: (data: ColumnProps) => {
          // Build up an array of all possible ref num matches
          let refNums: ReferenceNumber[] = data.data?.referenceNumbers ?? [];
          if (isLineItemType) {
            refNums = refNums.concat(data.data?.lineItemReferenceNumbers ?? []);
          }
          if (isShipmentColumnProps(data) && isOrderType) {
            refNums = refNums.concat(data.data?.relatedReferenceNumbers ?? []);
          }

          // Find any matches in the array and map them to a single string
          return refNums
            .filter((refNum) => refNum.typeNaturalId === refNumNaturalId)
            .map((refNum) =>
              formatReferenceNumberValue(
                referenceNumberValueOptionsMap,
                clientRefNum,
                refNum,
              ),
            )
            .join(', ');
        },
      });
    }) ?? []
  );
});

const esReferenceNumberColumns = computed(() => {
  return referenceNumbers.value.map((clientRefNum) => {
    const refNumNaturalId = clientRefNum.referenceNumberType?.naturalId!;

    return new ColumnSpecification({
      key: `referenceNumbers.${refNumNaturalId}.values.keyword`,
      field: `referenceNumbers.${refNumNaturalId}.values.keyword`,
      header: clientRefNum.referenceNumberType?.name,
      sortable: true,
      body: ({ data: order }: EsOrderColumnProps) => {
        // Build up an array of all possible ref num matches
        const refNums: Array<EsReferenceNumber | undefined> = [];

        refNums.push(order.referenceNumbers?.[refNumNaturalId]);
        order.lineItems?.forEach((l) =>
          refNums.push(l.referenceNumbers?.[refNumNaturalId]),
        );

        // Find any matches in the array and map them to a single string
        return refNums
          .flatMap((refNum) => refNum?.values)
          .filter((x) => !!x?.trim())
          .join(', ');
      },
    });
  });
});

const resolvedOrderColumns: Ref<ColumnSpecification[]> = computed(() => {
  return filterLocationGroupColumns(
    isElasticsearchEnabled.value ? ES_ORDER_COLUMNS : ORDER_COLUMNS,
  );
});

const resolvedRefNumColumns: Ref<ColumnSpecification[]> = computed(() =>
  isElasticsearchEnabled.value
    ? esReferenceNumberColumns.value
    : referenceNumberColumns.value,
);

const availableColumns: Ref<ColumnSpecification[]> = computed(() => {
  return [...resolvedOrderColumns.value, ...resolvedRefNumColumns.value].filter(
    distinctBy('key'),
  );
});

const availableColumnsByKey = computed(
  () => new Map(availableColumns.value.map((col) => [col.key, col])),
);

const availableColumnsByAlias = computed(() => {
  const aliasMap = new Map<string, ColumnSpecification>();
  for (const col of availableColumns.value) {
    for (const alias of col.alias ?? []) {
      aliasMap.set(alias, col);
    }
  }
  return aliasMap;
});

const onCriteriaChange = (newVal: OrderSearchCriteria) => {
  criteria.value = new OrderSearchCriteria(newVal);
  criteria.value.page = 0;
  isEdited.value = true;
};

const onPage = (event: DataTablePageEvent) => {
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Order | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
};

const saveSearch = async (savedSearchInfo: {
  name: string;
  defaultSearch: boolean;
}) => {
  const search = new OrderSavedSearch({
    name: savedSearchInfo.name,
    searchData: new OrderSearchCriteria(criteria.value),
    searchColumnKeys: columns.value.map((col) => col.key),
    defaultSearch: savedSearchInfo.defaultSearch,
  });
  const savedSearch = await createSavedSearch(search);
  if (savedSearch) {
    selectedSearch.value = savedSearch;
    isEdited.value = false;
  }
};

const onSaveSearchUpdate = async (savedSearch: OrderSavedSearch) => {
  const search = new OrderSavedSearch({
    ...savedSearch,
    searchData: new OrderSearchCriteria(criteria.value),
    searchColumnKeys: columns.value.map((col) => col.key),
  });
  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = savedSearch;
    isEdited.value = false;
  }
};

const onSaveSearchAsDefault = async (savedSearch: OrderSavedSearch) => {
  const search = new OrderSavedSearch({
    ...savedSearch,
    defaultSearch: true,
  });

  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = updatedSearch;
    isEdited.value = false;
  }
};

const onRemoveSearchAsDefault = async (savedSearch: OrderSavedSearch) => {
  const search = new OrderSavedSearch({
    ...savedSearch,
    defaultSearch: false,
  });
  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = updatedSearch;
    isEdited.value = false;
  }
};

const onSavedSearchDelete = (savedSearch: OrderSavedSearch) => {
  if (!savedSearch.id) {
    return;
  }

  deleteSavedSearch(savedSearch.id);
  selectedSearch.value = undefined;
  criteria.value = new OrderSearchCriteria();
  isEdited.value = false;
};

const onSavedSearchClear = () => {
  criteria.value = new OrderSearchCriteria();
  columns.value = getColumnsFromKeys(columnsLSKey.value);
  isEdited.value = false;
};

const onSavedSearchSelect = (savedSearch: OrderSavedSearch) => {
  criteria.value = new OrderSearchCriteria({
    ...savedSearch.searchData,
    savedSearchId: savedSearch.id,
  });
  columns.value = getColumnsFromKeys(columnsLSKey.value, savedSearch);
  isEdited.value = false;
};

const onSavedSearchDiscard = (savedSearch?: OrderSavedSearch) => {
  criteria.value = new OrderSearchCriteria(savedSearch?.searchData);

  const showAllColumns = !savedSearch || savedSearch.isPopularSearch;
  columns.value = showAllColumns
    ? availableColumns.value
    : getColumnsFromKeys(columnsLSKey.value, savedSearch);

  isEdited.value = false;
};

const onBatchActionCommand = ({ action }: ActionCommand<OrderAction>) => {
  if (selectedRows.value === undefined) {
    toast.addWarning({ detail: t('batchAction.noSelection') });
    return;
  }

  if (action === OrderAction.ADD_TAGS) {
    onBatchAddTags();
  } else if (action === OrderAction.CANCEL) {
    onBatchCancel();
  } else if (action === OrderAction.CLONE) {
    onBatchClone();
  } else if (action === OrderAction.REQUEST_SPOT_MARKET_BID) {
    onInviteCarriersToSpotMarket();
  } else if (action === OrderAction.ASSIGN_USERS) {
    const ownerId = selectedRows.value[0].owner?.id ?? 0;
    onBatchAssignUsers(ownerId);
  } else {
    toast.addWarning({
      detail: t('batchAction.paramNotSupported', {
        param: action,
      }),
    });
    selectedRows.value = [];
  }
};

const onBatchActionSuccess = async (toastMessage?: ToastMessage) => {
  results.value.loading = true;
  selectedRows.value = [];

  if (isElasticsearchEnabled.value) {
    await new Promise((resolve) => setTimeout(resolve, BATCH_ACTION_DELAY));
  }

  if (toastMessage) {
    toast.addSuccess(toastMessage);
  }

  runSearchDebounced();
};

const onBatchAddTags = () => {
  loadTags();
  showEditTagsModal.value = true;
};

const onBatchAddTagsConfirm = async (newTags: string[]) => {
  try {
    isBatchActionLoading.value = true;

    const orderIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.orderBatch.addTags(orderIds, newTags);

    loadTags({ bypassCache: true });
    onBatchActionSuccess({
      detail: t('messages.successAssigningParam', {
        param: t('tags.tags', newTags.length),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch adding tags', e);
    toast.addError({
      detail: t('errors.assigningParam', {
        param: t('tags.tags', newTags.length),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchCancel = () => {
  isCancellationModalVisible.value = true;
};

const onBatchCancelConfirm = async (event: CancelOrderPayload) => {
  if (!event.cancellationReason) {
    return;
  }

  const numOrders = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const orderIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.orderBatch.cancel(orderIds, {
      orderIds,
      cancellationReason: event.cancellationReason,
      cancellationComments: event.cancellationComments,
    });

    onBatchActionSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('orders.orders', numOrders),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch canceling orders', e);
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('orders.orders', numOrders),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchClone = () => {
  batchCloneOrderModal.value.showRefernceNumberOption = selectedRows.value.some(
    (o) => {
      if (o instanceof OrderSearchData) {
        return !!o.referenceNumbers?.length;
      } else {
        const refNums = o.referenceNumbers ?? {};
        return Object.values(refNums).length > 0;
      }
    },
  );

  batchCloneOrderModal.value.showTagsOption = selectedRows.value.some(
    (o) => !!o.tags?.length,
  );

  batchCloneOrderModal.value.visible = true;
};

const onBatchCloneConfirm = async () => {
  try {
    isBatchActionLoading.value = true;
    const orderIds = selectedRows.value.map((row) => row.id).filter(notNull);

    batchCloneSuccessVm.value.orderIds = await api.orderBatch.clone({
      orderIds,
      cloneReferenceNumbers: batchCloneOrderModal.value.cloneReferenceNumbers,
      cloneTags: batchCloneOrderModal.value.cloneTags,
      skipAutoActions: batchCloneOrderModal.value.skipAutoActions,
    });
    batchCloneSuccessVm.value.visible = true;
    onBatchActionSuccess();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error cloning orders', e);
    toast.addError({
      detail: t('orders.errors.cloning', selectedRows.value.length),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const getCanViewUpliftPermission = async () => {
  try {
    canViewUplift.value = await api.user.getHasUpliftPermission();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching uplift permission', e);
    canViewUplift.value = false;
  }
};

const onInviteCarriersToSpotMarket = () => {
  getCanViewUpliftPermission();
  inviteCarriersToSpotMarketModal.value.visible = true;
  inviteCarriersToSpotMarketModal.value.inviteCommand.orderIds =
    selectedRows.value.map((row) => row.id).filter(notNull);
};

const onInviteCarriersToSpotMarketConfirm = async (command: InviteCommand) => {
  const numOrders = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const { erroredSpotMarkets } = await inviteCarriersToSpotMarket(command);
    inviteCarriersToSpotMarketModal.value.visible = false;

    const numExpectedSpotMarkets = numOrders * command.scacs.length;
    const numSuccesses = numExpectedSpotMarkets - erroredSpotMarkets.length;
    if (numSuccesses) {
      onBatchActionSuccess();
    }

    if (erroredSpotMarkets.length) {
      spotMarketErrorModal.value.erroredSpotMarkets = erroredSpotMarkets;
      spotMarketErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // nothing to do here
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchAssignUsers = async (domainId: number) => {
  assignUsersModal.value.assignableUsers = loadAssignableUsersByDomainId(
    domainId,
    'order',
  );
  assignUsersModal.value.visible = true;
};

const onBatchAssignUsersConfirm = async (
  selectedUsers: UserRef[],
  type: UserAssignmentType,
) => {
  try {
    isBatchActionLoading.value = true;

    const orderIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.orderBatch.assignUsers(orderIds, selectedUsers, type);

    onBatchActionSuccess({
      detail: t('messages.successAssigningParam', {
        param: t('users.users', selectedUsers.length),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.assigningParam', {
        param: t('common.users', selectedUsers.length),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const getPopularSearchStorageKey = (
  savedSearch: OrderSavedSearch,
  lsKey: string,
) => {
  return `${lsKey}-${savedSearch.name?.replaceAll(' ', '_')}`;
};

const onEditedColumns = (newColumns: ColumnSpecification[]) => {
  // Not handled in watcher because internal state updates by itself
  isEdited.value = true;

  // Not handled in watcher because columns change before selected search
  const isAllSearch = !selectedSearch.value;
  if (isAllSearch) {
    localStorage.setItem(
      columnsLSKey.value,
      ColumnSpecification.serialize(newColumns),
    );
  } else if (selectedSearch.value?.isPopularSearch) {
    localStorage.setItem(
      getPopularSearchStorageKey(selectedSearch.value, columnsLSKey.value),
      ColumnSpecification.serialize(newColumns),
    );
  }
};

const onRefreshNoCache = () => {
  useCache.value = false;
  runSearchDebounced();
};

const getColumnFromJSON = (value: string) =>
  availableColumnsByKey.value.get(value) ??
  availableColumnsByAlias.value.get(value);

const getColumnsFromKeys = (
  lsKey: string,
  savedSearch?: OrderSavedSearch,
): ColumnSpecification[] => {
  // User is on user-made saved search with saved columns
  if (savedSearch?.searchColumnKeys.length) {
    return savedSearch.searchColumnKeys.map(getColumnFromJSON).filter(notNull);
  }

  let storageColumns: ColumnSpecification[] | undefined;
  if (savedSearch && savedSearch.isPopularSearch) {
    // User is on a default search that should have its own local storage values
    storageColumns = ColumnSpecification.deserialize(
      localStorage.getItem(getPopularSearchStorageKey(savedSearch, lsKey)),
      getColumnFromJSON,
    );
    // The default search hasn't been used before so load their "All" search columns
    if (storageColumns === undefined) {
      storageColumns = ColumnSpecification.deserialize(
        localStorage.getItem(lsKey),
        getColumnFromJSON,
      );
    }
  } else {
    // User is on "All" search or an outdated saved search with no saved columns
    storageColumns = ColumnSpecification.deserialize(
      localStorage.getItem(lsKey),
      getColumnFromJSON,
    );
  }

  if (storageColumns === undefined) {
    // User has no local storage so show all columns
    storageColumns = availableColumns.value;
  }

  return storageColumns;
};

const findSelectedSearch = (savedSearchId: number | undefined) => {
  if (!savedSearchId) {
    return;
  }

  const allSearches = [
    ...(popularSearches.value.data ?? []),
    ...(savedSearches.value.data ?? []),
  ];
  return allSearches.find(({ id }) => id === savedSearchId);
};

const loadAndSetSelectedSearch = async (lsKey: string) => {
  await Promise.all([loadPopularSearches(), loadSavedSearches()]);
  selectedSearch.value = findSelectedSearch(criteria.value.savedSearchId);
  columns.value = getColumnsFromKeys(lsKey, selectedSearch.value);
};

const loadColumnsAndSearch = async (lsKey: string) => {
  // Domain can be undefined on first load so wait until its correct
  if (!selectedDomain.value) {
    return;
  }

  await Promise.all([
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.ORDER }),
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.LINE_ITEM }),
  ]);

  if (searchQueryString.value) {
    // URL already has search params, fire search immediately
    void loadAndSetSelectedSearch(lsKey);
    void runSearch(criteria.value);
    return;
  }

  await Promise.all([loadPopularSearches(), loadSavedSearches()]);

  const defaultSearch = savedSearches.value.data?.find(
    (search) => search.defaultSearch,
  );
  if (defaultSearch) {
    selectedSearch.value = defaultSearch;
    criteria.value = new OrderSearchCriteria({
      ...defaultSearch.searchData,
      savedSearchId: defaultSearch.id,
    });
    columns.value = getColumnsFromKeys(lsKey, defaultSearch);
    isEdited.value = false;
    void runSearch(criteria.value);
    return;
  }

  selectedSearch.value = findSelectedSearch(criteria.value.savedSearchId);
  columns.value = getColumnsFromKeys(lsKey, selectedSearch.value);
  void runSearch(criteria.value);
};

watch(selectedSearch, (newVal: OrderSavedSearch | undefined) => {
  criteria.value.savedSearchId = newVal?.id;
});

watch(
  columnsLSKey,
  (newVal: string) => {
    loadColumnsAndSearch(newVal);
  },
  { immediate: true },
);

watch(
  isLocationGroupingEnabled,
  (enabled) => {
    if (enabled) {
      loadLocationGroups({ includeVisible: true });
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
@import '~@rygen/rygen-ui/assets/styles/animation/flash.css';
.order-list {
  .search-result-table-and-filter-wrapper {
    &.new-search-toggle {
      --app-chrome-main-content-header-height: 5.2rem;
    }
  }
}
</style>
